import { z } from 'zod';

import { defineQuery } from '@nesso/common-contracts/define';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { practiceIdSchema, practiceLocationIdSchema, practiceMemberIdSchema } from './ids';
import {
    appointmentModalitySchema,
    placeOfServiceCodeSchema,
    US_STATE_NAMES,
} from './practice.models';

export const GET_PRACTICE_OWNER_TYPE = 'practice/get-owner';

export const practiceOwnerSchema = z.object({
    id: practiceMemberIdSchema.meta(metadata.nonSensitive),
    email: z.string(),
});

export type PracticeOwner = z.input<typeof practiceOwnerSchema>;

export const getPracticeOwner = defineQuery(
    GET_PRACTICE_OWNER_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    practiceOwnerSchema,
);

export const GET_PRACTICE_BY_ID_TYPE = 'practice/get-by-id';

export const practiceSchema = z.object({
    id: practiceIdSchema.meta(metadata.nonSensitive),
    name: z.string().meta(metadata.nonSensitive),
});

export type Practice = z.input<typeof practiceSchema>;

export const getPracticeById = defineQuery(
    GET_PRACTICE_BY_ID_TYPE,
    z.object({
        id: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    practiceSchema,
);

export const GET_PRACTICE_DETAILS_TYPE = 'practice/get-details';

export const practiceDetailsSchema = z.object({
    id: practiceIdSchema.meta(metadata.nonSensitive),
    name: z.string().meta(metadata.nonSensitive),
    businessEmail: z.string().nullable().meta(metadata.nonSensitive),
    businessPhone: z.string().nullable().meta(metadata.nonSensitive),
    // No `nonSensitive` on input or output. The sanitizer strips it from every Access entry.
    taxId: z.string().nullable(),
});

export type PracticeDetails = z.input<typeof practiceDetailsSchema>;

export const getPracticeDetails = defineQuery(
    GET_PRACTICE_DETAILS_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    practiceDetailsSchema,
);

export const LIST_PRACTICE_LOCATIONS_TYPE = 'practice/list-locations';

export const practiceLocationSchema = z.object({
    id: practiceLocationIdSchema.meta(metadata.nonSensitive),
    name: z.string().meta(metadata.nonSensitive),
    street1: z.string().meta(metadata.nonSensitive),
    street2: z.string().nullable().meta(metadata.nonSensitive),
    city: z.string().meta(metadata.nonSensitive),
    state: z.enum(US_STATE_NAMES).meta(metadata.nonSensitive),
    zip: z.string().meta(metadata.nonSensitive),
    isDefault: z.boolean().meta(metadata.nonSensitive),
    placeOfServiceCode: placeOfServiceCodeSchema.meta(metadata.nonSensitive),
    offersTelehealth: z.boolean().meta(metadata.nonSensitive),
    defaultAppointmentModality: appointmentModalitySchema.nullable().meta(metadata.nonSensitive),
});

export type PracticeLocation = z.input<typeof practiceLocationSchema>;

export const listPracticeLocations = defineQuery(
    LIST_PRACTICE_LOCATIONS_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    z.array(practiceLocationSchema),
);
